import React, { useState } from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { staticURL } from 'core/url';
import { fromLocalStorage, toLocalStorage } from 'core/localStorage';
import { clientStore } from 'apps/client/app';

import Heading from 'components/Heading';
import Paragraph from 'components/Paragraph';
import Button from 'components/Button';
import { Link } from 'core/routing';
import Box from 'components/Box';

import {
  HideButtonWrapper,
  SectionBannerWrapper,
  MainContentWrapper,
  ImageWrapper,
} from './SectionBanner.styled';

class Visibility {
  @observable visible = true;

  @action SetVisible = bool => {
    this.visible = bool;
  };
}

const SectionBanner = observer(props => {
  const [store] = useState(() => new Visibility());
  const { visible, SetVisible } = store;
  const { isMobile } = clientStore;
  const { bannerId } = props;

  // Passing an optional "bannerId" prop to the SectionBanner adds an "X"
  // button for clicking it away. If the user has clicked the "X" button we
  // set "clickedDontShowAgain: true" to localStorage and don't render the
  // SectionBanner.
  if (fromLocalStorage(bannerId) || !visible) {
    return null;
  }

  const {
    imageSrc,
    linkName,
    title,
    children,
    description,
    buttonText,
    buttonHandler,
  } = props;

  const externalLinkMode = !!(
    linkName?.includes('http') || linkName?.includes('www')
  );
  const button = (
    <Button
      size={!isMobile ? 'default' : 'xsmall'}
      appearance='hero'
      icon='arrow_right'
      text={buttonText}
      onClick={buttonHandler}
    />
  );

  return (
    <SectionBannerWrapper>
      {bannerId && (
        <HideButtonWrapper>
          <Button
            outline
            variant='minimal'
            icon='close_small'
            size='small'
            onClick={() => {
              SetVisible(false);
              toLocalStorage(bannerId, { clickedDontShowAgain: true }, true);
            }}
          />
        </HideButtonWrapper>
      )}
      {imageSrc !== '' && (
        <ImageWrapper>
          <img
            src={imageSrc.includes('http') ? imageSrc : staticURL(imageSrc)}
          />
        </ImageWrapper>
      )}
      <MainContentWrapper>
        {title !== '' && (
          <Heading level='4' size={!isMobile ? 'large' : 'medium'}>
            {title}
          </Heading>
        )}
        <Box my={4}>{children}</Box>
        {description !== '' && (
          <Box my={4}>
            <Paragraph size={!isMobile ? 'default' : 'small'}>
              {description}
            </Paragraph>
          </Box>
        )}
        {buttonText && (
          <>
            {externalLinkMode ? (
              <a href={linkName} target='_blank' rel='noreferrer'>
                {button}
              </a>
            ) : (
              <Link
                name={linkName}
                params={props.linkParams}
                onClick={() => props.eventCallback && props.eventCallback()}
              >
                {button}
              </Link>
            )}
          </>
        )}
      </MainContentWrapper>
    </SectionBannerWrapper>
  );
});

export default SectionBanner;

SectionBanner.defaultProps = {
  imageSrc: '',
  title: '',
  description: '',
  buttonText: '',
  linkName: '',
  linkParams: {},
};

SectionBanner.propTypes = {
  imageSrc: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  buttonText: PropTypes.string,
  buttonHandler: PropTypes.func,
  linkName: PropTypes.string,
  linkParams: PropTypes.object,
  bannerId: PropTypes.string,
  paddingTop: PropTypes.string,
  paddingBottom: PropTypes.string,
};

/*
Render Value-Prop-Banner (CAD Collaboration) =>

+ React normal props render

<SectionBanner
  imageSrc='assets/banners/001.svg'
  title='Ready for real-time CAD Collaboration?'
  description='Our 3D viewer just got a total upgrade.'
  buttonText='Try it out'
  linkName: 'initiative.story'
  linkParams={{
    initiative: '+wikifactory',
    slug: 'take-your-cad-files-online-with-the-new-online-3d-viewer',
  }}
/>

or

+ Object destructuring assignment

const sectionBannerProps = {
  imageSrc: 'assets/banners/001.svg',
  title: 'Ready for real-time CAD Collaboration?',
  description: 'Our 3D viewer just got a total upgrade.',
  buttonText: 'Try it out',
  linkName: 'initiative.story',
  linkParams: {
    initiative: '+wikifactory',
    slug: 'take-your-cad-files-online-with-the-new-online-3d-viewer',
  },
};

<SectionBanner {...sectionBannerProps} />

---

+ Inside the app imageSrc should be 'assets/...' and inside the storybook imageSrc should not have 'assets/'

+ The storybook does not fully support core components yet. When we click on the button if it is not an external link inside the storybook is normal happen a redirect to http://localhost:6006/+wikifactory/stories/take-your-cad-files-online-with-the-new-online-3d-viewer
If we replace 'localhost:6006' for 'wikifactory.com' that will be the final output inside the web app environment.

+ In conclusion the component will work better inside the web app environment
*/
